import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, MapPin, CheckCircle, ArrowRight, Shield, Star, Clock, Wrench } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getCityBySlug } from '../data/cityData';

const serviceIcons = [Shield, Wrench, Clock, Star];

export const CityServicePage = () => {
  const { citySlug } = useParams();
  const city = getCityBySlug(citySlug);

  if (!city) {
    return (
      <div className="pt-32 pb-16 text-center">
        <h1 className="text-3xl font-bold text-slate-900">City not found</h1>
        <Link to="/cities-we-serve" className="text-sky-500 mt-4 inline-block">View all cities we serve</Link>
      </div>
    );
  }

  return (
    <div data-testid={`city-page-${city.slug}`}>
      <Helmet>
        <title>{`Pool Service in ${city.name}, FL | A-Quality Pool Service`}</title>
        <meta name="description" content={`Professional pool cleaning, repair, and maintenance in ${city.name}, ${city.county}, FL. A-Quality Pool Service - Licensed & Insured. Call 813-453-5988 for a free quote.`} />
        <meta name="keywords" content={`${city.name} pool service, ${city.name} pool cleaning, ${city.name} pool repair, ${city.name} pool maintenance, pool service ${city.name} FL`} />
        <link rel="canonical" href={`https://a-qualitypools.net/pool-service/${city.slug}`} />
      </Helmet>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-sky-600 to-sky-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <MapPin className="h-4 w-4 text-white mr-2" />
              <span className="text-white text-sm font-medium">{city.county}, Florida</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Pool Service in {city.name}
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">{city.description}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-sky-700 hover:bg-slate-100 font-semibold px-8" data-testid="city-get-quote-btn">
                  Get Free Quote
                </Button>
              </Link>
              <a href="tel:813-453-5988">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8" data-testid="city-call-btn">
                  <Phone className="h-4 w-4 mr-2" /> 813-453-5988
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro + Highlights */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                  Trusted Pool Care in {city.name}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">{city.intro}</p>
                <ul className="space-y-3">
                  {city.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-sky-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Quick Contact</h3>
                <p className="text-slate-600 mb-4">Ready to get started with pool service in {city.name}? Contact us today for a free estimate.</p>
                <div className="space-y-3">
                  <a href="tel:813-453-5988" className="flex items-center gap-3 text-slate-700 hover:text-sky-600 transition-colors">
                    <Phone className="h-5 w-5 text-sky-500" />
                    <span className="font-medium">813-453-5988</span>
                  </a>
                  <div className="flex items-center gap-3 text-slate-700">
                    <MapPin className="h-5 w-5 text-sky-500" />
                    <span>Serving {city.name}, {city.county}</span>
                  </div>
                </div>
                <Link to="/contact" className="block mt-6">
                  <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="city-contact-btn">
                    Request Free Estimate <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                Our Services in {city.name}
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">Comprehensive pool services for {city.name} homeowners and businesses</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {city.services.map((service, i) => {
                const Icon = serviceIcons[i];
                return (
                  <Card key={i} className="border-slate-200 hover:border-sky-500 transition-colors hover:shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-sky-50 rounded-xl">
                          <Icon className="h-6 w-6 text-sky-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-slate-900 mb-2">{service.title}</h3>
                          <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 text-center">
              {city.name} Areas We Serve
            </h2>
            <p className="text-slate-600 text-center mb-8">
              We provide pool service throughout {city.name} and surrounding neighborhoods
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {city.neighborhoods.map((n, i) => (
                <span key={i} className="px-4 py-2 bg-sky-50 text-sky-700 rounded-full text-sm font-medium">{n}</span>
              ))}
            </div>

            {/* Nearby Cities */}
            <div className="border-t border-slate-200 pt-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 text-center">Also Serving Nearby Cities</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {city.nearby.map((n, i) => {
                  const slug = n.toLowerCase().replace(/['\s]+/g, '-').replace(/--/g, '-');
                  return (
                    <Link key={i} to={`/pool-service/${slug}`} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-sky-50 hover:text-sky-700 transition-colors">
                      {n} Pool Service
                    </Link>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="pt-16 pb-32 bg-sky-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready for Premium Pool Service in {city.name}?
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              Contact A-Quality Pool Service today for a free estimate. Licensed & Insured — License # CPC1460475
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-sky-700 hover:bg-slate-100 font-semibold px-8">
                  Get Free Quote
                </Button>
              </Link>
              <a href="tel:813-453-5988">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8">
                  <Phone className="h-4 w-4 mr-2" /> Call 813-453-5988
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CityServicePage;
